/**
 * Meeting briefings – pure helpers for the Briefings page, the briefing
 * branch of the review detail page and the triage panel.
 *
 * A briefing is a review of kind `briefing`: an employee's prep for a
 * meeting. The meeting ends it, not an approval – before it the owner reads
 * and comments, after it (phase `held`) the owner closes it with a few
 * lines of debrief or without. Day boundaries are Berlin's. Runtime-free imports
 * only, so `node --test` runs this file as it is.
 */
import type { AdminReviewDoc } from "../api/admin";
import type { PillTone } from "../components/Pill";
import type { ConfirmOptions } from "../components/confirm/context";

type BriefingFields = Pick<
  AdminReviewDoc,
  "status" | "phase" | "meeting_at" | "meeting_end_at" | "seen_version" | "content_version"
>;

const TZ = "Europe/Berlin";
const DAY_KEY = new Intl.DateTimeFormat("en-CA", { timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit" });
const WEEKDAY = new Intl.DateTimeFormat("en-GB", { timeZone: TZ, weekday: "short" });
const DAY_MONTH = new Intl.DateTimeFormat("de-DE", { timeZone: TZ, day: "2-digit", month: "2-digit" });
const DAY_MONTH_YEAR = new Intl.DateTimeFormat("de-DE", { timeZone: TZ, day: "2-digit", month: "2-digit", year: "numeric" });
const TIME = new Intl.DateTimeFormat("de-DE", { timeZone: TZ, hour: "2-digit", minute: "2-digit", hourCycle: "h23" });

/** Epoch ms of a server instant; naive timestamps are UTC. NaN when unreadable. */
function instantMs(iso: string | null | undefined): number {
  if (!iso) return NaN;
  return Date.parse(/[Zz]|[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso}Z`);
}

/** The Berlin calendar day of an instant, "2026-11-04". */
export function berlinDayKey(ms: number): string {
  return DAY_KEY.format(new Date(ms));
}

/** Whole calendar days from one day key to another. */
function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

/** Pending or changes requested – the meeting has not closed it yet. */
export function isOpenBriefing(doc: Pick<AdminReviewDoc, "status">): boolean {
  return doc.status === "pending" || doc.status === "changes_requested";
}

/** The owner has not opened the current version. */
export function isUnseen(doc: Pick<AdminReviewDoc, "seen_version" | "content_version">): boolean {
  return doc.seen_version == null || doc.seen_version < doc.content_version;
}

/** An open briefing that waits on the owner: its debrief is open, or a
 * version the owner has not read waits. */
export function briefingNeedsOwner(doc: BriefingFields): boolean {
  if (!isOpenBriefing(doc)) return false;
  return doc.phase === "held" || (doc.status === "pending" && isUnseen(doc));
}

export type BriefingGroupId = "debrief" | "today" | "week" | "later";

export interface BriefingGroup<T> {
  id: BriefingGroupId;
  name: string;
  docs: T[];
}

const GROUP_ORDER: BriefingGroupId[] = ["debrief", "today", "week", "later"];
const GROUP_NAME: Record<BriefingGroupId, string> = {
  debrief: "Debrief open",
  today: "Today",
  week: "Next 7 days",
  later: "Later",
};

/** The group of an open briefing: held → "Debrief open", else by the
 * Berlin day of its meeting. A meeting already running counts as today. */
export function briefingGroupId(doc: BriefingFields, nowMs: number): BriefingGroupId {
  if (doc.phase === "held") return "debrief";
  const start = instantMs(doc.meeting_at);
  if (isNaN(start)) return "later";
  const days = daysBetween(berlinDayKey(nowMs), berlinDayKey(start));
  if (days <= 0) return "today";
  return days <= 7 ? "week" : "later";
}

/** Open briefings in groups, in group order; each keeps the server's order
 * (held first, then by meeting time). Empty groups are left out. */
export function groupBriefings<T extends BriefingFields>(rows: T[], nowMs: number): BriefingGroup<T>[] {
  const byId = new Map<BriefingGroupId, T[]>();
  for (const row of rows) {
    const id = briefingGroupId(row, nowMs);
    byId.set(id, [...(byId.get(id) ?? []), row]);
  }
  return GROUP_ORDER.filter((id) => byId.has(id)).map((id) => ({ id, name: GROUP_NAME[id], docs: byId.get(id) ?? [] }));
}

function dayLabel(ms: number, nowMs: number): string {
  const date = new Date(ms);
  const sameYear = berlinDayKey(ms).slice(0, 4) === berlinDayKey(nowMs).slice(0, 4);
  return `${WEEKDAY.format(date)} ${(sameYear ? DAY_MONTH : DAY_MONTH_YEAR).format(date)}`;
}

/** "Wed 04.11. 11:00–11:30" in Berlin time; across midnight both days,
 * the year when it is not this year's, the start alone without an end. */
export function meetingLabel(
  doc: Pick<AdminReviewDoc, "meeting_at" | "meeting_end_at">,
  nowMs: number = Date.now(),
): string {
  const start = instantMs(doc.meeting_at);
  if (isNaN(start)) return "no meeting time";
  const head = `${dayLabel(start, nowMs)} ${TIME.format(new Date(start))}`;
  const end = instantMs(doc.meeting_end_at);
  if (isNaN(end) || end <= start) return head;
  return berlinDayKey(end) === berlinDayKey(start)
    ? `${head}–${TIME.format(new Date(end))}`
    : `${head} – ${dayLabel(end, nowMs)} ${TIME.format(new Date(end))}`;
}

/** "45m", "3h", "3h 20m". */
function shortSpan(ms: number): string {
  const minutes = Math.max(1, Math.round(ms / 60_000));
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

/** Where an open briefing's meeting stands: "in 3h 20m", "tomorrow",
 * "in 2 days", "in progress", "held – debrief open". Null once closed. */
export function briefingPhaseText(doc: BriefingFields, nowMs: number): string | null {
  if (!isOpenBriefing(doc)) return null;
  if (doc.phase === "held") return "held – debrief open";
  const start = instantMs(doc.meeting_at);
  if (isNaN(start)) return null;
  if (start <= nowMs) return "in progress";
  const days = daysBetween(berlinDayKey(nowMs), berlinDayKey(start));
  if (days === 0) return `in ${shortSpan(start - nowMs)}`;
  return days === 1 ? "tomorrow" : `in ${days} days`;
}

/** The status word of a briefing – the meeting's vocabulary, not approval's. */
export function briefingStatus(doc: Pick<AdminReviewDoc, "status" | "phase">): { label: string; tone: PillTone } {
  if (doc.status === "done") return { label: "closed", tone: "muted" };
  if (!isOpenBriefing(doc)) return { label: doc.status, tone: "muted" };
  if (doc.phase === "held") return { label: "debrief open", tone: "signal" };
  if (doc.status === "changes_requested") return { label: "changes requested", tone: "muted" };
  return { label: "upcoming", tone: "muted" };
}

/**
 * The briefing to open after sending comments on or closing `current`: the
 * first other open briefing that waits on the owner, in the list's order
 * (held first, then by meeting time). `undefined` – back to the list.
 */
export function nextBriefing<T extends BriefingFields & Pick<AdminReviewDoc, "id">>(
  rows: T[],
  current: Pick<AdminReviewDoc, "id">,
): T | undefined {
  return rows.find((row) => row.id !== current.id && briefingNeedsOwner(row));
}

export interface CloseContext {
  author?: string | null;
  /** The meeting is over (phase `held`). */
  held: boolean;
  hasNotes: boolean;
}

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** The question before closing a briefing – after the meeting with or
 * without a debrief, before it as "not needed". */
export function closeQuestion(ctx: CloseContext): ConfirmOptions {
  const author = capitalize(ctx.author ?? "the employee");
  if (!ctx.held) {
    return {
      title: "Close the briefing before the meeting?",
      body: `${author} learns the briefing is not needed${ctx.hasNotes ? " and gets your note" : ""}. It moves to History.`,
      confirmLabel: "Close",
    };
  }
  return ctx.hasNotes
    ? {
        title: "Close the briefing with your debrief?",
        body: `${author} gets your notes and follows up on them. The briefing moves to History.`,
        confirmLabel: "Close with debrief",
      }
    : {
        title: "Close the briefing without a debrief?",
        body: `${author} learns the meeting took place, nothing more. The briefing moves to History.`,
        confirmLabel: "Close",
      };
}

/** The sentence after a close. */
export function closeOutcome(ctx: CloseContext): string {
  if (!ctx.held) return "Briefing closed – not needed";
  return ctx.hasNotes ? `Briefing closed – ${ctx.author ?? "the employee"} follows up on your debrief` : "Briefing closed";
}
